import { useMutation } from "@tanstack/react-query";
import { useFormik } from "formik";
import moment from "moment";
import React, { useState, useRef, useEffect } from "react";
import { Card, Form } from "react-bootstrap";
import "react-international-phone/style.css";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AsyncPaginate } from "react-select-async-paginate";
import * as images from "../../assets/image";
import DatePicker from "react-datepicker";
import * as yup from "yup";
import NavigateBack from "../../common/NavigateBack";
import {
  addPayout,
  getEvents,
  getUserByEvents,
  getUsers,
  uploadSignFile,
} from "../../services/services";
import { constant } from "../../utils/constants";
import { base64toImageFile, setTimeFromString } from "../../utils/function";
import { toastAlert } from "../../utils/SweetAlert";
import SignatureCanvas from "react-signature-canvas";
import useDetails from "../../hooks/useDetails";
import useRole from "../../hooks/useRole";

const AddPayOut = () => {
  const dateRef = useRef();
  const details = useDetails();
  const role = useRole();

  const signRef = useRef(null);
  const [signed, setSigned] = useState(null);

  const location = useLocation();
  const navigate = useNavigate();

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    setValues,
    touched,
    handleSubmit,
  } = useFormik({
    initialValues: {
      clientId: "",
      eventId: "",
      employeeId: details.role == constant.ROLE.EMPLOYEE ? { label: `${details.firstName} ${details.lastName}`, value: details._id } : "",
      date: new Date(),
      assignmentDate: new Date(),
      checkIn: "",
      checkOut: "",
      note: "",
    },
    validationSchema: yup.object().shape({
      date: yup.date().required().label("Shift Date"),
      clientId: yup.object().shape({
        value: yup.string().required().label("Client"),
      }),
      eventId: yup.object().shape({
        value: yup.string().required().label("Shift Time"),
      }),
      employeeId: yup.object().shape({
        value: yup.string().required().label("Employee"),
      }),
      checkIn: yup.string().required().label("Check In"),
      checkOut: yup
        .string()
        .required()
        .label("Checkout time")
        .test(
          "is-greater-than-startTime",
          "check out time should be later than start time",
          function (value) {
            const { checkIn } = this.parent;
            const start = new Date(checkIn);
            const end = new Date(value);
            return end > start;
          }
        ),
    }),

    onSubmit: async (values) => {
      if (!signed) {
        toastAlert("error", "Please sign before saving");
        return null;
      }

      const file = base64toImageFile(signed);
      let formData = new FormData();
      formData.append("file", file, "signature.png");

      const imageUpload = await uploadSignFile(formData);
      if (imageUpload.status !== 200) {
        toastAlert("error", "error uploading signature file");
        return null;
      }
      let body = {
        employeeId: values?.employeeId?.value,
        eventId: values.eventId.value,
        note: values?.note?.trim() !== "" ? values?.note.trim() : "",
        checkIn: moment(values.checkIn)?.format("HH:mm"),
        checkOut: moment(values.checkOut)?.format("HH:mm"),
        signature: imageUpload.data.data.fileUrl,
      };
      mutation.mutate(body);
    },
  });

  const mutation = useMutation({
    mutationFn: async (body) => addPayout(body),
    onSuccess: (resp) => {
      toastAlert("success", resp?.data?.message);
      navigate(-1);
    },
  });

  const getEmployee = async (search, loadedOptions, { page }) => {
    let params = {
      page: page,
      limit: constant.PAGE_LIMIT,
      role: constant.ROLE.EMPLOYEE,
      sortedBy: constant.SORTED_BY.FIRST_NAME,
      sortBy: constant.SORT_BY.ASC,
    };
    if (search) params.search = search;
    let resp = await getUsers(params);
    let array = (await resp?.data?.data?.users) ?? [];

    return {
      options: array.map((i) => ({
        label: `${i.firstName} ${i.lastName}`,
        value: i._id,
      })),
      hasMore: resp?.data?.data?.total_pages > page,
      additional: {
        page: page + 1,
      },
    };
  };

  const getClients = async (search, loadedOptions, { page }) => {
    let params = {
      page: page,
      limit: constant.PAGE_LIMIT,
      employeeId: values?.employeeId?.value,
      role: constant.ROLE.CLIENT,
    };
    if (search) params.search = search;
    let resp = await getUserByEvents(params);
    let array = (await resp?.data?.data) ?? [];

    return {
      options: array.map((i) => ({
        label: `${i?.userDetails?.firstName} ${i?.userDetails?.lastName}`,
        value: i?.userDetails?._id,
      })),
      hasMore: false,
      additional: {
        page: 1,
      },
    };
  };

  const getEventsList = async (search, loadedOptions, { page }) => {
    let params = {
      page: page,
      employeeId: values?.employeeId?.value,
      limit: constant.PAGE_LIMIT,
      clientId: values?.clientId?.value,
      sortedBy: "startTime",
      sortBy: "ASC",
      acceptedEvents: "true",
    };

    const start = moment(values.assignmentDate).format(constant.DATE_ONLY_FORMAT);
    params.range = `${start}TO${start}`;

    if (search) params.search = search;

    let resp = await getEvents(params);
    let array = (await resp?.data?.data?.events) ?? [];
    return {
      options: array.map((i) => {
        const startTime = moment(i.startTime);
        const endTime = moment(i.endTime);
        const labelValue = `${startTime.format("ll")} (${startTime.format(
          constant.HOURS_FORMAT
        )} - ${endTime.format(constant.HOURS_FORMAT)})`;
        return {
          label: labelValue,
          value: i._id,
          date: i?.startTime,
          startTime: i?.startTime,
          endTime: i?.endTime,
        };
      }),
      hasMore: resp?.data?.data?.total_pages > page,
      additional: {
        page: page + 1,
      },
    };
  };

  const parseTimeInput = (input, shiftDate = new Date()) => {
    let hours,
      minutes = 0;
    const isPM = /pm$/i.test(input);
    const isAM = /am$/i.test(input);

    const timeString = input.replace(/(am|pm)/i, "").trim();
    if (timeString.length === 1) {
      hours = parseInt(timeString[0], 10);
    } else if (timeString.length === 2) {
      hours = parseInt(timeString, 10);
    } else if (timeString.length === 3) {
      hours = parseInt(timeString[0], 10);
      minutes = parseInt(timeString.slice(1), 10);
    } else if (timeString.length === 4) {
      hours = parseInt(timeString.slice(0, 2), 10);
      minutes = parseInt(timeString.slice(2), 10);
    } else {
      return null;
    }

    if (isPM && hours < 12) hours += 12;
    if (isAM && hours === 12) hours = 0;

    const resultDate = new Date(shiftDate || Date.now());
    resultDate.setHours(hours, minutes, 0, 0);

    return resultDate;
  };

  const handleClear = () => {
    if (signRef.current && signRef.current.getCanvas()) {
      const canvas = signRef.current.getCanvas();
      const ctx = canvas.getContext('2d');
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.restore();
      setSigned(null);
      setTimeout(() => {
        resizeCanvas();
      }, 50);
    }
  };

  const saveSignature = () => {
    if (signRef && signRef.current) {
      const isEmpty = signRef.current.isEmpty();
      if (isEmpty) {
        toastAlert("error", "Please sign before saving");
        return null;
      }
      const signurl = signRef.current.getCanvas().toDataURL("image/png");
      setSigned(signurl);
    } else toastAlert("error", "Some error occurred with signature canvas");
  };

  useEffect(() => {
    if (location.state && location.state.formValues && Object.keys(location.state.formValues).length)
      setValues({ ...values, ...location.state.formValues });
  }, []);

  // Debounce utility
  const debounce = (func, wait) => {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => func(...args), wait);
    };
  };

  // Canvas resize function
  const resizeCanvas = () => {
    if (signRef.current) {
      const canvas = signRef.current.getCanvas();
      const context = canvas.getContext("2d");
      const rect = canvas.getBoundingClientRect();
      const devicePixelRatio = window.devicePixelRatio || 1;
      const targetHeight = Math.min(Math.max(rect.width / 2.6667, 225), 450);
      
      // Save current signature if it exists
      let tempSignature = null;
      if (!signRef.current.isEmpty()) {
        tempSignature = canvas.toDataURL("image/png");
      }
      
      // Update dimensions
      canvas.width = rect.width * devicePixelRatio;
      canvas.height = targetHeight * devicePixelRatio;
      context.scale(devicePixelRatio, devicePixelRatio);
      canvas.style.height = `${targetHeight}px`;
      
      console.log("Resize - Width:", rect.width, "Height:", targetHeight);
      
      // Restore signature
      if (tempSignature) {
        signRef.current.fromDataURL(tempSignature, { width: rect.width, height: targetHeight });
      } else if (signed) {
        signRef.current.fromDataURL(signed, { width: rect.width, height: targetHeight });
      }
    }
  };

  // Initial canvas scaling
  useEffect(() => {
    const timer = setTimeout(resizeCanvas, 0); // Run after DOM render
    return () => clearTimeout(timer);
  }, []);

  // Handle window resize
  useEffect(() => {
    const debouncedResize = debounce(resizeCanvas, 100);
    window.addEventListener("resize", debouncedResize);
    return () => window.removeEventListener("resize", debouncedResize);
  }, []);

  // Restore signature and resize when signed changes
  useEffect(() => {
    if (signed && signRef.current) {
      resizeCanvas(); // Re-run resize to ensure correct dimensions
    }
  }, [signed]);

  useEffect(() => {
    const textarea = document.getElementById("notes");
    textarea.style.height = "auto"; // Reset height to recalculate
    textarea.style.height = `${textarea.scrollHeight}px`; // Adjust to content
  }, [values.note]);

  return (
    <>
      <section className="main-content">
        {/* Mobile-specific styles */}
        <style>
          {`
            @media screen and (max-width: 768px) {
              .sigCanvas {
                min-height: 180px !important;
                max-height: 300px !important;
              }
            }
          `}
        </style>

        <div className="addclientPage">
          <div className="addHeader mb-3">
            <span className="heading20">Time sheet</span>
          </div>
          <Card
            border="light"
            bg="light"
            className="p-4 w-50 mx-auto timeSheetFrom"
            style={{ overflow: "visible" }}
          >
            <Form onSubmit={handleSubmit}>
              <div className="clientInputBox">
                <div className="row">
                  {role === constant.ROLE.ADMIN && (
                    <div className="col-md-12 mb-1">
                      <div className="form-group">
                        <label className="labelTxt">
                          Employee <span className="text-danger">*</span>
                        </label>
                        <AsyncPaginate
                          additional={{
                            page: 1,
                          }}
                          styles={constant.REACT_SELECT_CUSTOM_STYLE}
                          debounceTimeout={500}
                          loadOptions={getEmployee}
                          value={values?.employeeId}
                          onChange={(e) => {
                            setFieldValue("employeeId", e);
                            setFieldValue("clientId", "");
                            setFieldValue("eventId", "");
                            setFieldValue("checkIn", "");
                            setFieldValue("checkOut", "");
                          }}
                          onBlur={() => setFieldTouched("employeeId", true)}
                        />

                        <small className="text-danger requiredTxt">
                          {touched.employeeId && errors?.employeeId?.value}
                        </small>
                      </div>
                    </div>
                  )}
                  {values.employeeId && (
                    <div className="col-md-12 mb-1">
                      <div className="form-group">
                        <label className="labelTxt">
                          Client <span className="text-danger">*</span>
                        </label>
                        <AsyncPaginate
                          additional={{
                            page: 1,
                          }}
                          styles={constant.REACT_SELECT_CUSTOM_STYLE}
                          debounceTimeout={500}
                          loadOptions={getClients}
                          key={values?.employeeId?.value}
                          value={values?.clientId}
                          isDisabled={location?.state?.formValues}
                          onChange={(e) => {
                            setFieldValue("clientId", e);
                            setFieldValue("eventId", "");
                            setFieldValue("checkIn", "");
                            setFieldValue("checkOut", "");
                          }}
                          onBlur={() => setFieldTouched("clientId", true)}
                        />

                        <small className="text-danger requiredTxt">
                          {touched.clientId && errors?.clientId?.value}
                        </small>
                      </div>
                    </div>
                  )}

                  {values.clientId && (
                    <div className="col-md-12 mb-1">
                      <div className="form-group position-relative">
                        <label className="labelTxt">
                          Shift Date<span className="text-danger">*</span>
                        </label>
                        <img
                          src={images.calendar}
                          className="calanderIcon"
                          alt="calendarImg"
                          role="button"
                          onClick={() => dateRef.current.setFocus()}
                        />

                        <DatePicker
                          selected={values?.assignmentDate}
                          onChange={(date) => {
                            setFieldValue("assignmentDate", date);
                            setFieldValue("eventId", "");
                            setFieldValue("date", "");
                            setFieldValue("checkIn", "");
                            setFieldValue("checkOut", "");
                          }}
                          maxDate={new Date()}
                          onBlur={() => setFieldTouched("assignmentDate", true)}
                          className="inputBox"
                          ref={dateRef}
                        />
                      </div>
                    </div>
                  )}
                  {values.clientId && (
                    <div className="col-md-12 mb-1">
                      <div className="form-group">
                        <label className="labelTxt">
                          Shift Time <span className="text-danger">*</span>
                        </label>
                        <AsyncPaginate
                          additional={{
                            page: 1,
                          }}
                          styles={constant.REACT_SELECT_CUSTOM_STYLE}
                          debounceTimeout={500}
                          key={`${values?.clientId?.value}-${values?.assignmentDate}`}
                          loadOptions={getEventsList}
                          value={values?.eventId}
                          isDisabled={location?.state?.formValues}
                          onChange={(e) => {
                            setFieldValue("eventId", e);
                            setFieldValue("date", e.date);
                            setFieldValue("checkIn", new Date(e?.startTime));
                            setFieldValue("checkOut", new Date(e?.endTime));
                          }}
                          onBlur={() => setFieldTouched("eventId", true)}
                        />
                        <small className="text-danger requiredTxt">
                          {touched.eventId && errors?.eventId?.value}
                        </small>
                      </div>
                    </div>
                  )}

                  <div className="col-md-6 mb-1">
                    <div className="form-group">
                      <label className="labelTxt">
                        Check-In
                        <span className="text-danger">*</span>
                      </label>

                      <DatePicker
                        selected={values?.checkIn}
                        onChange={(date) => {
                          if (date && values?.date) {
                            const combinedDate = new Date(values.date);
                            combinedDate.setHours(date.getHours());
                            combinedDate.setMinutes(date.getMinutes());
                            combinedDate.setSeconds(0, 0);
                            setFieldValue("checkIn", combinedDate);
                          }
                        }}
                        onChangeRaw={(e) => {
                          const input = e.target.value.trim();
                          if (/^\d{1,4}([ap]m)?$/i.test(input)) {
                            const parsedDate = parseTimeInput(input, values?.date);
                            if (parsedDate) {
                              setFieldValue("checkIn", parsedDate);
                            }
                          }
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        className="inputBox"
                        onBlur={() => setFieldTouched("checkIn", true)}
                      />

                      <small className="text-danger requiredTxt">
                        {touched.checkIn && errors.checkIn}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-6 mb-1">
                    <div className="form-group">
                      <label className="labelTxt">
                        Check-Out
                        <span className="text-danger">*</span>
                      </label>

                      <DatePicker
                        selected={values?.checkOut}
                        onChange={(date) => {
                          if (date && values?.date) {
                            const combinedDate = new Date(values.date);
                            combinedDate.setHours(date.getHours());
                            combinedDate.setMinutes(date.getMinutes());
                            combinedDate.setSeconds(0, 0);
                            setFieldValue("checkOut", combinedDate);
                          }
                        }}
                        onChangeRaw={(e) => {
                          const input = e.target.value.trim();
                          if (/^\d{1,4}([ap]m)?$/i.test(input)) {
                            const parsedDate = parseTimeInput(input, values?.date);
                            if (parsedDate) {
                              setFieldValue("checkOut", parsedDate);
                            }
                          }
                        }}
                        showTimeSelect
                        showTimeSelectOnly
                        timeIntervals={15}
                        timeCaption="Time"
                        dateFormat="h:mm aa"
                        className="inputBox"
                        onBlur={() => setFieldTouched("checkOut", true)}
                      />

                      <small className="text-danger requiredTxt">
                        {touched.checkOut && errors.checkOut}
                      </small>
                    </div>
                  </div>

                  <div className="col-md-12 mb-1">
                    <div className="form-group">
                      <label className="labelTxt">Note</label>
                      <textarea
                        rows={1}
                        id="notes"
                        name="note"
                        className="inputBox overflow-hidden"
                        value={values?.note}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />

                      <small className="text-danger requiredTxt">
                        {touched.note && errors.note}
                      </small>
                    </div>
                  </div>
                  <div className="col-md-12 mb-1" style={{ overflow: "visible", minHeight: "300px" }}>
                    <label className="labelTxt">Client Initials</label>
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        overflow: "visible",
                      }}
                    >
                      <SignatureCanvas
                        ref={signRef}
                        penColor="green"
                        canvasProps={{
                          className: "sigCanvas",
                          width: 1200,
                          height: 450,
                        }}
                        style={{
                          width: "100%",
                          height: "auto",
                          aspectRatio: "2.6667 / 1",
                          minHeight: "225px",
                          maxHeight: "450px",
                          position: "relative",
                          display: "block",
                          boxSizing: "border-box",
                          touchAction: "none",
                        }}
                        onEnd={saveSignature}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "15px",
                        marginBottom: "15px",
                      }}
                    >
                      {/* <button
                        onClick={handleClear}
                        type="button"
                        className="cancleBtn"
                        style={{
                          padding: "6px 12px",
                          backgroundColor: "#797979",
                          color: "#ffffff",
                          borderRadius: "7px",
                          border: "none",
                          cursor: "pointer",
                        }}
                      >
                        Clear
                      </button> */}
                    </div>
                  </div>
                </div>
              </div>
              <div className="staffBtn mt-0">
                <button className="addBtn w-100" type="submit">
                  Submit
                </button>
              </div>
            </Form>
          </Card>
        </div>
      </section>
    </>
  );
};

export default AddPayOut;